import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import type { PhomemoSettings, LabelSpec } from '../printers/phomemo/types';
import { mergeLabelSpec } from '../printers/phomemo/types';

const PHOMEMO_SETTINGS_KEY = 'phomemo_settings';

export function usePhomemoSettings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState<PhomemoSettings>({});
  const [loading, setLoading] = useState(true);

  const effectiveLabelSpec: LabelSpec = useMemo(
    () => mergeLabelSpec(settings?.labelSpec),
    [settings?.labelSpec]
  );

  const loadSettings = useCallback(async () => {
    if (!user) {
      setSettings({});
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_settings')
        .select('phomemo_settings')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      const stored = (data?.phomemo_settings as PhomemoSettings | null) ?? null;
      if (stored && typeof stored === 'object') {
        setSettings(stored);
      } else {
        const local = localStorage.getItem(`${PHOMEMO_SETTINGS_KEY}_${user.id}`);
        if (local) {
          try {
            const parsed = JSON.parse(local) as PhomemoSettings;
            setSettings(parsed);
          } catch {
            setSettings({});
          }
        } else {
          setSettings({});
        }
      }
    } catch {
      const local = user ? localStorage.getItem(`${PHOMEMO_SETTINGS_KEY}_${user.id}`) : null;
      if (local) {
        try {
          setSettings(JSON.parse(local) as PhomemoSettings);
        } catch {
          setSettings({});
        }
      } else {
        setSettings({});
      }
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const saveSettings = useCallback(async (next: PhomemoSettings) => {
    setSettings(next);
    if (!user) return;
    try {
      const { error } = await supabase
        .from('user_settings')
        .upsert(
          {
            user_id: user.id,
            phomemo_settings: next,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'user_id' }
        );
      if (error) throw error;
    } catch {
      localStorage.setItem(`${PHOMEMO_SETTINGS_KEY}_${user.id}`, JSON.stringify(next));
    }
  }, [user?.id]);

  return { settings, loading, saveSettings, loadSettings, effectiveLabelSpec };
}
